import { useEffect, useMemo, useState } from 'react';
import { getOrganizations, getVendors, type OrgOption, type PortSpec, type VendorOption } from '../lib/api';
import { CONNECTOR_TYPES, DEFAULT_CONNECTOR } from '../lib/connectors';

interface Props {
  onClose: () => void;
  onSubmit: (payload: {
    charger_id: string;
    name: string;
    lat: number;
    lng: number;
    location: string;
    vendor_id?: string;
    org_id?: string;
    power_kw: number;
    is_public: boolean;
    ports: PortSpec[];
  }) => Promise<void>;
}

export default function AddChargerModal({ onClose, onSubmit }: Props) {
  const [chargerId, setChargerId] = useState('');
  const [name, setName] = useState('');
  const [lat, setLat] = useState('17.4400');
  const [lng, setLng] = useState('78.4900');
  const [location, setLocation] = useState('');
  const [orgId, setOrgId] = useState<string>('');
  const [vendorId, setVendorId] = useState<string>('');
  const [powerKw, setPowerKw] = useState('3.3');
  const [isPublic, setIsPublic] = useState(true);
  const [ports, setPorts] = useState<PortSpec[]>([
    { port_number: 1, connector_type: DEFAULT_CONNECTOR },
    { port_number: 2, connector_type: DEFAULT_CONNECTOR },
  ]);

  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [o, v] = await Promise.all([getOrganizations(), getVendors()]);
        if (cancelled) return;
        setOrgs(o);
        setVendors(v);
      } catch (err: any) {
        // Non-fatal — user can still submit without a vendor/org.
        console.warn('Failed to load orgs/vendors', err?.message);
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter vendors to only those in the selected org (backend rejects
  // cross-org vendor assignments). If no org selected, show all so the
  // user can pick a vendor first and their org auto-fills.
  const visibleVendors = useMemo(() => {
    if (!orgId) return vendors;
    return vendors.filter((v) => v.org_id === orgId);
  }, [vendors, orgId]);

  function updatePortCount(newCount: number) {
    const clamped = Math.max(1, Math.min(4, newCount));
    setPorts((prev) => {
      const next: PortSpec[] = [];
      for (let i = 0; i < clamped; i++) {
        next.push(prev[i] ?? { port_number: i + 1, connector_type: DEFAULT_CONNECTOR });
      }
      return next;
    });
  }

  function updatePortConnector(index: number, connector_type: string) {
    setPorts((prev) => prev.map((p, i) => (i === index ? { ...p, connector_type } : p)));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const latN = Number(lat);
      const lngN = Number(lng);
      const powerN = Number(powerKw);
      if (!Number.isFinite(latN) || !Number.isFinite(lngN)) throw new Error('Lat/Lng must be numbers');
      if (!Number.isFinite(powerN) || powerN <= 0) throw new Error('Max power must be a positive number');

      await onSubmit({
        charger_id: chargerId.trim(),
        name: name.trim() || chargerId.trim(),
        lat: latN,
        lng: lngN,
        location: location.trim(),
        vendor_id: vendorId || undefined,
        org_id: orgId || undefined,
        power_kw: powerN,
        is_public: isPublic,
        ports,
      });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="modal" style={{ maxWidth: 560 }} onSubmit={submit}>
        <h2>Add simulator charger</h2>

        <div className="field">
          <label>Charger ID <span className="muted">(unique, e.g. SIMV1001)</span></label>
          <input value={chargerId} onChange={(e) => setChargerId(e.target.value.toUpperCase())} required />
        </div>

        <div className="field">
          <label>Display name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional — defaults to Charger ID" />
        </div>

        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Latitude</label>
            <input value={lat} onChange={(e) => setLat(e.target.value)} required />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Longitude</label>
            <input value={lng} onChange={(e) => setLng(e.target.value)} required />
          </div>
        </div>

        <div className="field">
          <label>Address / location</label>
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Optional" />
        </div>

        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Organization</label>
            <select
              value={orgId}
              onChange={(e) => {
                const nextOrg = e.target.value;
                setOrgId(nextOrg);
                // Clear vendor if it doesn't belong to the new org.
                if (vendorId) {
                  const v = vendors.find((x) => x.id === vendorId);
                  if (v && v.org_id !== nextOrg) setVendorId('');
                }
              }}
              disabled={loadingOptions}
            >
              <option value="">— None —</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Vendor</label>
            <select
              value={vendorId}
              onChange={(e) => {
                const id = e.target.value;
                setVendorId(id);
                // Auto-fill org if picking a vendor while org is blank.
                if (id && !orgId) {
                  const v = vendors.find((x) => x.id === id);
                  if (v?.org_id) setOrgId(v.org_id);
                }
              }}
              disabled={loadingOptions}
            >
              <option value="">— None —</option>
              {visibleVendors.map((v) => (
                <option key={v.id} value={v.id}>{v.business_name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Max power (kW)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={powerKw}
              onChange={(e) => setPowerKw(e.target.value)}
              required
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Visibility</label>
            <select value={isPublic ? 'public' : 'private'} onChange={(e) => setIsPublic(e.target.value === 'public')}>
              <option value="public">Public — visible to all customers</option>
              <option value="private">Private — hidden from map</option>
            </select>
          </div>
        </div>

        <div className="field">
          <label>Ports</label>
          <div className="row" style={{ gap: 8, marginBottom: 8 }}>
            <span className="muted" style={{ flex: '0 0 auto' }}>Count:</span>
            {[1, 2, 3, 4].map((n) => (
              <button
                key={n}
                type="button"
                className={ports.length === n ? '' : 'ghost'}
                onClick={() => updatePortCount(n)}
                style={{ padding: '4px 12px', flex: '0 0 auto' }}
              >{n}</button>
            ))}
          </div>
          <datalist id="connector-types">
            {CONNECTOR_TYPES.map((c) => <option key={c} value={c} />)}
          </datalist>
          <div style={{ display: 'grid', gap: 6 }}>
            {ports.map((p, i) => (
              <div key={p.port_number} className="row" style={{ gap: 8 }}>
                <span className="muted" style={{ width: 70 }}>Port {p.port_number}</span>
                <input
                  list="connector-types"
                  value={p.connector_type}
                  onChange={(e) => updatePortConnector(i, e.target.value)}
                  placeholder={DEFAULT_CONNECTOR}
                  style={{ flex: 1 }}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="row mt" style={{ gap: 8 }}>
          <button type="submit" disabled={busy} style={{ flex: 1 }}>{busy ? 'Creating…' : 'Create'}</button>
          <button type="button" className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
        </div>
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}
